import cls from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState, useCallback } from 'react';
import { WorkspaceBanner } from 'src/components/base/WorkspaceBanner';
import { VerticalSplitter } from 'src/components/base/Splitter';
import { useFetchTraps, useUpdateTrap } from 'src/model/traps';
import { useCurrentWorkspace } from 'src/model/workspaces';
import { useEffectState } from 'src/utils/hooks';
import { hasPermission, PERMISSION_TRAP_EDIT } from 'src/api/permissions';
import { useSystemStore } from 'src/stores';

import { isValidPosition } from 'src/utils/helpers';

import { SidePanel } from './SidePanel';
import { MapPanel } from './MapPanel';

import classnames from './TrapsMap.module.scss';

export const TrapsMap = observer(() => {
  const [center, setCenter] = useState();
  const [minimized, setMinimized] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [selectedItem, setSelectedItem] = useState();
  const { workspace: { workspace_id } } = useCurrentWorkspace();
  const { workspaces, workspace: currentWorkspaceDetails, user } = useSystemStore();

  const resolveWorkspacePermissions = useCallback((targetWorkspaceId) => {
    if (Array.isArray(workspaces)) {
      const match = workspaces.find(({ workspace_id: id }) => id === targetWorkspaceId);
      if (match) {
        return match.permissions;
      }
    }

    if (currentWorkspaceDetails?.workspace_id === targetWorkspaceId) {
      return currentWorkspaceDetails.permissions;
    }

    return user?.permissions;
  }, [workspaces, currentWorkspaceDetails, user]);

  const canEditTrap = useCallback((targetWorkspaceId) => (
    hasPermission(resolveWorkspacePermissions(targetWorkspaceId), PERMISSION_TRAP_EDIT)
  ), [resolveWorkspacePermissions]);

  const { data, mutate } = useFetchTraps({ workspace_id, pageSize: 1000 });
  const [updateTrapAction] = useUpdateTrap(mutate);

  const [items, setItems] = useEffectState(() => data.items.map(({ trap_id, trap_name, event_priority, is_caught, event_name, serial_no, latitude, longitude, workspace_id: itemWorkspaceId }, index) => ({
    index, trap_id, trap_name, serial_no, event_priority, is_caught, event_name,
    position: { lat: latitude, lng: longitude },
    changed: { lat: latitude, lng: longitude },
    workspaceId: Number.isFinite(Number(itemWorkspaceId)) ? Number(itemWorkspaceId) : itemWorkspaceId ?? null,
  })), [data.items]);

  function handleCancel() {
    setItems(items.map((item) => {
      item.changed = item.position;
      return item;
    }));
    setEditMode(false);
    setSelectedItem(null);
  }

  async function handleSaveChanges() {
    await Promise.all(items.filter(item => canEditTrap(item.workspaceId) && isValidPosition(item.changed) && (item.changed.lat !== item.position.lat || item.changed.lng !== item.position.lng))
      .map(({ trap_id, changed }) => updateTrapAction({ trap_id, latitude: changed.lat, longitude: changed.lng }))
    );

    setEditMode(false);
    setSelectedItem(null);

    await mutate();
  }

  async function handleDropItem(dropped) {
    if (!center || !canEditTrap(dropped.workspaceId)) { return; }
    Object.assign(dropped, { changed: center });
    setItems([...items]);
    setSelectedItem(dropped);
    await updateTrapAction({ trap_id: dropped.trap_id, latitude: center.lat, longitude: center.lng });
  }

  const handleItemPositionChange = useCallback(async (item) => {
    if (!canEditTrap(item.workspaceId)) { return; }
    const pos = item.changed;
    await updateTrapAction({ trap_id: item.trap_id, latitude: pos?.lat ?? '', longitude: pos?.lng ?? '' });
  }, [canEditTrap, updateTrapAction]);

  return (
    <div>
      <WorkspaceBanner />

      <div className={cls('d-flex flex-row flex-grow-1', classnames.root)}>

        <div className={cls(classnames.page, { 'd-none': minimized })}>
          <SidePanel items={items}
            selectedItem={selectedItem} onSelectItem={setSelectedItem}
            editMode={editMode} onEditMode={setEditMode} onDropItem={handleDropItem}
            onCancel={handleCancel} onSubmit={handleSaveChanges}
            canEditItem={item => canEditTrap(item?.workspaceId)}
          />
        </div>

        <VerticalSplitter onClick={() => setMinimized(!minimized)} />

        <MapPanel items={items} editMode={editMode} onCenterChanged={setCenter}
          selectedItem={selectedItem} onSelectItem={setSelectedItem}
          onItemPositionChange={handleItemPositionChange} canEditItem={item => canEditTrap(item?.workspaceId)}
        />

      </div>

    </div>
  )
})
